<template>
  <div
    v-if="isLoading || isLoadingRecognitions"
    class="d-flex flex-grow-1 justify-space-between"
  >
    <div class="d-flex ps-5 py-3">
      <v-skeleton-loader type="avatar" />
      <div class="d-flex flex-column ms-6">
        <v-skeleton-loader type="text" width="150px" />
        <v-skeleton-loader type="text" width="150px" />
        <v-skeleton-loader type="text" width="150px" />
        <div class="d-flex">
          <v-skeleton-loader type="chip" class="me-2" />
          <v-skeleton-loader type="chip" class="me-2" />
          <v-skeleton-loader type="chip" class="me-2" />
        </div>
      </div>
    </div>
    <v-skeleton-loader type="chip" class="me-2" />
  </div>

  <div v-else class="d-flex justify-space-between">
    <div class="d-flex ps-5 py-3">
      <div class="avatar">
        <UserAvatar
          data-private
          :account="profileAccount"
          :isLoading="isLoading"
          :canChangePhoto="isSelfProfile"
          :canDeletePhoto="isSelfProfile"
          @photoUploaded="$emit('photoUploaded', $event)"
        />
      </div>
      <div class="d-flex flex-column ms-6">
        <p class="mx-0 mt-0 mb-2 pa-0 headline" data-private>
          {{ profileAccount.nickname || getFullName(profileAccount) }}
        </p>
        <p
          v-if="profileAccount.position"
          class="body-1 blue-grey--text text--darken-1 ma-0 pa-0"
        >
          {{ profileAccount.position }}
        </p>
        <div v-if="profileAccount.isDeleted" class="d-flex">
          <v-chip small color="grey lighten-4 ">
            {{ $t('people.status.deactivated') }}
          </v-chip>
        </div>
        <div class="d-flex align-center mt-2">
          <v-icon size="24" color="blue-grey">mdi-medal-outline</v-icon>
          <p class="ma-0 pa-0 text-body-2 blue-grey--text text-darken-1 ms-3">
            {{ recognitionsCount }}
          </p>
        </div>
        <UserRoleChips class="mt-4" :account="profileAccount" />
      </div>
    </div>
    <div class="me-4">
      <Button
        v-if="isSelfProfile"
        color="primary"
        id="profile-editProfile"
        data-tourid="profile-button-editProfile"
        data-testid="profile-button-editProfile"
        :text="$t('profile_settings.edit_profile')"
        :to="{ name: 'profileSettings' }"
        @click="
          trackProfileNavigate({
            element: 'editProfileButton',
            section: 'summary',
          })
        "
      />
    </div>
  </div>
</template>
<script>
import UserAvatar from '@/components/UserAvatar.vue'

import UserRoleChips from '@/components/UserRoleChips.vue'

import api from '@/api'

import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'AccountSummary',

  mixins: [userMixin, trackerMixin, mixin],

  components: {
    UserAvatar,
    UserRoleChips,
    Button,
  },

  props: {
    profileAccount: {
      required: true,
      type: Object,
    },

    isSelfProfile: {
      required: true,
      type: Boolean,
    },

    isLoading: {
      required: true,
      type: Boolean,
    },
  },

  data() {
    return {
      showRecognitionDialog: false,
      recognitions: { given: 0, received: 0 },
      isLoadingRecognitions: false,
    }
  },

  watch: {
    profileAccount: {
      deep: true,
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue?._id !== oldValue?._id) {
          return this.getRecognitionsSummary()
        }
      },
    },
  },

  computed: {
    receivedRecognitions() {
      return this.recognitions?.received ?? 0
    },

    recognitionsCount() {
      return this.$t('recognitions.recognitionWithCount', {
        count: this.receivedRecognitions,
      })
    },

    profileId() {
      return this.getRouteParams().userId
    },

    organizationId() {
      return this.getRouteParams().organizationId
    },
  },

  methods: {
    async getRecognitionsSummary() {
      if (!this.profileId) return
      try {
        this.isLoadingRecognitions = true
        const response = await api.recognition.getAccountSummary(
          this.organizationId,
          this.profileId
        )

        this.recognitions = response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoadingRecognitions = false
      }
    },
  },
}
</script>
